import { isNativeApp } from './native';

/**
 * «الدخول بالبصمة / Face ID» inside the Matix POS phone app.
 *
 * ⚠ THE UNIT STORED IS THE WHOLE SESSION, NOT A TOKEN. The till's session is a
 * multi-stage payload — token + subsidiary + branch + cash drawer + shift,
 * saved by `cashierAuthClient` under `pos:cashier:mt-auth` — and only its final
 * `stage: 'session'` form is usable. Storing the JWT alone (what the ERP shell
 * does, where a token IS the session) would unlock into an app that has a token
 * and no drawer.
 *
 * The password is NEVER stored. What goes into the device keystore is the
 * session the server already issued, and it dies with its own 8-hour `exp`.
 *
 * How the gate works: on a COLD start the saved session is moved out of
 * localStorage (`lockSessionOnColdStart`), so the till opens on the sign-in
 * screen and only a successful biometric check puts it back. A reload inside
 * the same running app does not lock again — `sessionStorage` survives a
 * reload and dies with the process, which is exactly the cold-start signal.
 *
 * Everything here is a no-op in a browser, and every call swallows its own
 * errors: a biometric problem must never block the ordinary password sign-in.
 */

const FLAG_KEY = 'matix-pos-native:biometric';
const DECLINED_KEY = 'matix-pos-native:biometric-declined';
const LAST_LOGIN_KEY = 'matix-pos-native:last-login';
const RUNNING_KEY = 'matix-pos-native:running';
const SECURE_SESSION = 'pos-session';

async function plugins() {
  const [{ BiometricAuth }, { SecureStorage }] = await Promise.all([
    import('@aparajita/capacitor-biometric-auth'),
    import('@aparajita/capacitor-secure-storage'),
  ]);
  return { BiometricAuth, SecureStorage };
}

function readLocal(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* storage unavailable */ }
}

/** Seconds-since-epoch `exp` of a JWT, or null when it has none. */
export function jwtExpiry(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * A session is only worth unlocking into if its token still has life in it.
 * The 60-second margin keeps a token that expires mid-unlock from landing the
 * cashier on a screen that 401s on the first tap.
 */
export function tokenUsable(token: string | null | undefined, nowMs: number = Date.now()): token is string {
  if (!token) return false;
  const exp = jwtExpiry(token);
  return exp !== null && exp * 1000 > nowMs + 60_000;
}

export function biometricEnabled(): boolean {
  return isNativeApp() && readLocal(FLAG_KEY) === '1';
}

export function biometricDeclined(): boolean {
  return readLocal(DECLINED_KEY) === '1';
}

/** The workspace last signed in on this device — never a password. */
export function lastLogin(): { slug: string } | null {
  if (!isNativeApp()) return null;
  try {
    const v = JSON.parse(readLocal(LAST_LOGIN_KEY) ?? 'null') as { slug?: unknown } | null;
    return v && typeof v.slug === 'string' ? { slug: v.slug } : null;
  } catch {
    return null;
  }
}

export async function biometricAvailable(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { BiometricAuth } = await plugins();
    return (await BiometricAuth.checkBiometry()).isAvailable;
  } catch {
    return false;
  }
}

/**
 * After every successful sign-in that reached `stage: 'session'`: remember the
 * workspace, and when biometrics is on, replace the stored session.
 * `sessionJson` is the serialised `AuthData` — the same bytes
 * `cashierAuthClient.saveAuth` writes.
 */
export async function afterPasswordLogin(sessionJson: string, slug: string): Promise<void> {
  if (!isNativeApp()) return;
  writeLocal(LAST_LOGIN_KEY, JSON.stringify({ slug }));
  if (!biometricEnabled()) return;
  try {
    const { SecureStorage } = await plugins();
    await SecureStorage.set(SECURE_SESSION, sessionJson);
  } catch { /* keystore unavailable — the till just keeps asking for a password */ }
}

export async function enableBiometric(sessionJson: string): Promise<boolean> {
  try {
    const { SecureStorage } = await plugins();
    await SecureStorage.set(SECURE_SESSION, sessionJson);
    writeLocal(FLAG_KEY, '1');
    writeLocal(DECLINED_KEY, null);
    return true;
  } catch {
    return false;
  }
}

export function declineBiometric(): void {
  writeLocal(DECLINED_KEY, '1');
}

export async function disableBiometric(): Promise<void> {
  writeLocal(FLAG_KEY, null);
  try {
    const { SecureStorage } = await plugins();
    await SecureStorage.remove(SECURE_SESSION);
  } catch { /* nothing stored */ }
}

/** The stored session if it is still usable — without asking for biometrics yet. */
export async function storedSession(): Promise<string | null> {
  if (!biometricEnabled()) return null;
  try {
    const { SecureStorage } = await plugins();
    const raw = await SecureStorage.get(SECURE_SESSION);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as { stage?: unknown; token?: unknown };
    // Only a completed session unlocks: a half-finished stage would drop the
    // cashier into a till with no drawer.
    if (parsed.stage !== 'session' || !tokenUsable(parsed.token as string)) return null;
    return raw;
  } catch {
    return null;
  }
}

/**
 * Ask for the fingerprint / face and, on success, return the stored session
 * JSON. Null when cancelled, failed, or the session has expired.
 */
export async function biometricUnlock(texts: {
  reason: string;
  cancel: string;
  title: string;
}): Promise<string | null> {
  const session = await storedSession();
  if (!session) return null;
  try {
    const { BiometricAuth } = await plugins();
    await BiometricAuth.authenticate({
      reason: texts.reason,
      cancelTitle: texts.cancel,
      androidTitle: texts.title,
      allowDeviceCredential: true,
    });
    return session;
  } catch {
    return null;
  }
}

/**
 * Called once at startup, BEFORE the app reads its session: on a COLD start
 * with biometrics on, the saved session is removed from localStorage so the
 * till opens on the sign-in screen and asks for the fingerprint / face first.
 *
 * `storageKey` is `cashierAuthClient`'s own key, passed in rather than
 * duplicated here so the two can never disagree about where the session lives.
 */
export function lockSessionOnColdStart(storageKey: string): void {
  if (!biometricEnabled()) return;
  try {
    if (window.sessionStorage.getItem(RUNNING_KEY)) return;
    window.sessionStorage.setItem(RUNNING_KEY, '1');
  } catch {
    return;
  }
  writeLocal(storageKey, null);
}
